/*
 * Modal for editing template placeholders and logo URLs (admin home tab).
 */

import React from "react";
import { FaTimes, FaSave } from "react-icons/fa";
import i18n from "../../../i18n/i18n.js";
import {
  extractPlaceholders,
  extractLogoPlaceholders,
  getTextDirection,
} from "../../utils/templateUtils.js";

export default function TemplateEditModal({
  editingTemplate,
  logoUrls,
  setLogoUrls,
  setChangesSaved,
  placeholderValues,
  onPlaceholderChange,
  selectedGroups,
  onCancel,
  onSavePlaceholders,
  loadingTemplates,
  substitutePlaceholders,
  replaceLogoPlaceholders,
  getTextDirection: getDir,
}) {
  if (!editingTemplate) return null;

  const dir = (getDir && getDir()) || getTextDirection(() => (typeof i18n !== "undefined" ? i18n.getLanguage() : "en"));
  const subjectPlaceholders = extractPlaceholders(editingTemplate.subject);
  const bodyPlaceholders = extractPlaceholders(editingTemplate.htmlBody);
  const allPlaceholders = [...new Set([...subjectPlaceholders, ...bodyPlaceholders])];
  const logoPlaceholders = [
    ...extractLogoPlaceholders(editingTemplate.subject),
    ...extractLogoPlaceholders(editingTemplate.htmlBody),
  ];
  const regularPlaceholders = allPlaceholders.filter((p) => !logoPlaceholders.includes(p));
  const groupName = selectedGroups.map((g) => g.displayName).join(", ") || i18n.t("home.previewGroup");

  return (
    <div className="fixed inset-0 bg-white/30 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div className={`flex items-center justify-between p-4 border-b border-gray-200 ${dir === "rtl" ? "flex-row-reverse" : ""}`}>
          <h2 className="text-xl font-semibold text-gray-900">
            {i18n.t("home.customizeTemplate")} {editingTemplate.name}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {logoPlaceholders.length > 0 && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">{i18n.t("home.logoUrlsSection")}</h3>
              </div>
              <div className="space-y-4">
                {logoPlaceholders.map((logoPlaceholder) => (
                  <div key={logoPlaceholder}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">{"{{" + logoPlaceholder + "}}"}</code>
                    </label>
                    <input
                      type="url"
                      value={logoUrls[logoPlaceholder] || ""}
                      onChange={(e) => {
                        setLogoUrls((prev) => ({ ...prev, [logoPlaceholder]: e.target.value }));
                        setChangesSaved(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://example.com/logo.png"
                    />
                    {logoUrls[logoPlaceholder] && (
                      <div className="mt-2">
                        <img
                          src={logoUrls[logoPlaceholder]}
                          alt="Logo preview"
                          className="max-h-20 border border-gray-300 rounded"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {regularPlaceholders.length > 0 && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">{i18n.t("home.fillPlaceholders")}</h3>
                <p className="text-xs text-blue-800">{i18n.t("home.placeholderInfo")}</p>
              </div>
              <div className="space-y-4">
                {regularPlaceholders.map((placeholder) => (
                  <div key={placeholder}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm">{"{{" + placeholder + "}}"}</code>
                    </label>
                    <input
                      type="text"
                      value={placeholderValues[placeholder] || ""}
                      onChange={(e) => onPlaceholderChange(placeholder, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={`${i18n.t("home.enterValueFor")} ${placeholder}`}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {regularPlaceholders.length === 0 && logoPlaceholders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>{i18n.t("home.noPlaceholders")}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{i18n.t("home.preview")}</label>
            <div className="mb-2 p-2 bg-gray-50 rounded border border-gray-200">
              <div className="text-xs font-medium text-gray-700 mb-1">{i18n.t("home.subject")}</div>
              <div className="text-sm text-gray-900">
                {substitutePlaceholders(replaceLogoPlaceholders(editingTemplate.subject, logoUrls), {
                  ...placeholderValues,
                  GROUP_NAME: groupName,
                })}
              </div>
            </div>
            <div
              className="border border-gray-300 rounded-md overflow-hidden bg-white p-4"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: substitutePlaceholders(
                    replaceLogoPlaceholders(editingTemplate.htmlBody, logoUrls),
                    { ...placeholderValues, GROUP_NAME: groupName }
                  ),
                }}
              />
            </div>
          </div>
        </div>

        <div className={`flex items-center gap-2 p-4 border-t border-gray-200 ${dir === "rtl" ? "flex-row-reverse" : "justify-end"}`}>
          <button
            onClick={onCancel}
            disabled={loadingTemplates}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {i18n.t("common.cancel")}
          </button>
          <button
            onClick={onSavePlaceholders}
            disabled={loadingTemplates}
            className={`px-4 py-2 bg-[#46A2F2] text-white rounded-md hover:bg-[#0C75D1] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${dir === "rtl" ? "flex-row-reverse" : ""}`}
          >
            {loadingTemplates ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {i18n.t("common.saving")}
              </>
            ) : (
              <>
                <FaSave className="w-4 h-4" />
                {i18n.t("home.saveValues")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
